/**
 * OAuth Service - OAuth Authentication Flows
 * T017: Implement OAuthService for OAuth flows
 */

import type { GatewayClient } from './client'
import type {
  OAuthProvider,
  AuthProvidersResponse,
  UserResponse,
  TokenRefreshResponse,
  MessageResponse,
} from './types'
import { isApiError, isAuthError } from './errors'

// ============================================================================
// Types
// ============================================================================

export interface OAuthService {
  /**
   * Get available OAuth providers
   */
  getProviders(): Promise<AuthProvidersResponse>

  /**
   * Get login URL for a specific provider
   * @param provider - OAuth provider name
   * @param redirectUrl - Optional URL to redirect to after login
   */
  getLoginUrl(provider: OAuthProvider, redirectUrl?: string): string

  /**
   * Get current authenticated user
   * Returns null if not authenticated
   */
  getCurrentUser(): Promise<UserResponse | null>

  /**
   * Refresh the access token using refresh token cookie
   */
  refreshToken(): Promise<TokenRefreshResponse>

  /**
   * Logout and clear authentication cookies
   */
  logout(): Promise<void>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create an OAuthService instance
 */
export function createOAuthService(client: GatewayClient): OAuthService {
  const baseUrl = client.config.baseUrl

  return {
    async getProviders(): Promise<AuthProvidersResponse> {
      return client.get<AuthProvidersResponse>('/auth/providers')
    },

    getLoginUrl(provider: OAuthProvider, redirectUrl?: string): string {
      // Use window.location.origin as fallback for empty baseUrl (local dev with proxy)
      const origin = baseUrl || window.location.origin
      const url = new URL(`/auth/${provider}/login`, origin)
      if (redirectUrl) {
        url.searchParams.set('return_url', redirectUrl)
      }
      return url.toString()
    },

    async getCurrentUser(): Promise<UserResponse | null> {
      try {
        return await client.get<UserResponse>('/v1/auth/me', { noRetry: true })
      } catch (error) {
        // Return null for auth errors (not authenticated)
        if (isApiError(error) && isAuthError(error)) {
          return null
        }
        throw error
      }
    },

    async refreshToken(): Promise<TokenRefreshResponse> {
      return client.post<TokenRefreshResponse>('/v1/auth/refresh')
    },

    async logout(): Promise<void> {
      await client.post<MessageResponse>('/auth/logout')
    },
  }
}
